import { CrossSellItemsResponseProps } from '../../store/ducks/cross-sell/types';
import { EGerdauService } from '../EGerdauService';
import { EndpointsMvp } from '../MVP/enum';
import { CrossSellItemProps, crossSellSkus } from '../../utils/cross-sell';
import { NewCartItem, SortProductsItem } from '../MVP/models';
import { AxiosInstance, AxiosResponse } from 'axios';

export class CrossSellService extends EGerdauService {
  constructor() {
    super();
  }

  async getCrossSellItems(props: {
    cartItems?: NewCartItem[];
    divisions?: string[];
    businessPartners: string[];
    soldTo?: string;
    commerceApi: AxiosInstance;
    useNewFlow: boolean;
    scope?: string;
  }): Promise<CrossSellItemsResponseProps> {
    const {
      cartItems,
      divisions,
      businessPartners,
      soldTo,
      commerceApi,
      useNewFlow,
      scope = 'checkout'
    } = props;

    if (!businessPartners?.length) {
      return { items: [] };
    }

    if (useNewFlow) {
      return this.getCrossSellItemsNewFlow({
        businessPartners,
        soldTo,
        commerceApi,
        scope
      });
    }

    const shipTo = businessPartners[0];
    const effectiveSoldTo = soldTo ?? businessPartners[0];

    return this.getCrossSellItemsLegacy({
      cartItems: cartItems ?? [],
      divisions: divisions ?? [],
      shipTo,
      soldTo: effectiveSoldTo,
      commerceApi
    });
  }

  async hasCrossSellItems(props: {
    businessPartners: string[];
    soldTo?: string;
    commerceApi: AxiosInstance;
    scope?: string;
  }): Promise<boolean> {
    const { businessPartners, soldTo, commerceApi, scope = 'checkout' } = props;
    const baseHeaders = super.getHeaders({ withCommerceToken: true });

    const response = await commerceApi.post(
      EndpointsMvp.HAS_CROSS_SELL_ITEMS(),
      { scope, businessPartners, ...(soldTo ? { soldTo } : {}) },
      { headers: baseHeaders }
    );

    return response.data === true;
  }

  private async getCrossSellItemsNewFlow(props: {
    businessPartners: string[];
    soldTo?: string;
    commerceApi: AxiosInstance;
    scope: string;
  }): Promise<CrossSellItemsResponseProps> {
    const { businessPartners, soldTo, commerceApi, scope } = props;
    const baseHeaders = super.getHeaders({ withCommerceToken: true });

    try {
      const response = await commerceApi.post(
        EndpointsMvp.CROSS_SELL_ITEMS(),
        { scope, businessPartners, ...(soldTo ? { soldTo } : {}) },
        { headers: baseHeaders }
      );

      const rawItems: SortProductsItem[] = Array.isArray(response.data)
        ? response.data
        : response.data?.items ?? [];

      const mappedItems = rawItems.slice(0, 8).map((item) => {
        try {
          const priceGerdau = JSON.parse(
            item.extension_attributes?.price_gerdau ?? '{}'
          );
          return { ...item, price: priceGerdau };
        } catch {
          return item;
        }
      });

      return {
        items: mappedItems,
        selected_business_partner: Array.isArray(response.data)
          ? undefined
          : response.data?.selected_business_partner
      };
    } catch (error) {
      console.error('Erro ao buscar itens de cross-sell:', error);
      return { items: [] };
    }
  }

  private async getCrossSellItemsLegacy(props: {
    cartItems: NewCartItem[];
    divisions: string[];
    shipTo: string;
    soldTo: string;
    commerceApi: AxiosInstance;
  }): Promise<CrossSellItemsResponseProps> {
    const headers = super.getHeaders();
    const { cartItems, divisions, shipTo, soldTo, commerceApi } = props;

    if (!cartItems || cartItems.length === 0) {
      return { items: [] };
    }

    const uniqueCartItems = Array.from(
      new Map(cartItems.map((item) => [item.sku, item])).values()
    );
    const cartSkus = uniqueCartItems.map((item) => item.sku);
    const matchedCrossSellSkus = crossSellSkus.filter((crossSellSku) =>
      cartSkus.includes(crossSellSku)
    );

    if (matchedCrossSellSkus.length === 0) {
      return { items: [] };
    }

    const MAX_LEGACY_FETCH_ITEMS = 8;
    const crossSellItems: CrossSellItemProps[] = [];

    for (const sku of matchedCrossSellSkus) {
      if (crossSellItems.length >= MAX_LEGACY_FETCH_ITEMS) break;

      try {
        const requestURL = EndpointsMvp.CROSS_SELL_PRODUCTS(sku);
        const response: AxiosResponse<CrossSellItemProps[]> =
          await commerceApi.get(requestURL, { headers });

        const items = response.data || [];

        if (items && items.length > 0) {
          const uniqueItems = items.filter(
            (item: CrossSellItemProps) =>
              !cartSkus.includes(item.linked_product_sku) &&
              !crossSellItems.some(
                (existingItem) => existingItem.sku === item.linked_product_sku
              )
          );

          const remaining = MAX_LEGACY_FETCH_ITEMS - crossSellItems.length;
          crossSellItems.push(...uniqueItems.slice(0, remaining));
        }
      } catch (error) {
        console.error(`Falha ao buscar cross-sell para o SKU: ${sku}`, error);
        continue;
      }
    }

    if (crossSellItems.length === 0) {
      return { items: [] };
    }

    const bodyRequest = { shipTo, soldTo };
    const params = { includeOptions: false, includePrices: true };

    try {
      const response = await commerceApi.post(
        `${EndpointsMvp.PRODUCTS_SORT}`,
        {
          ...bodyRequest,
          sortType: 'R',
          currentPage: 0,
          pageSize: crossSellItems.length,
          products: [],
          filter: {
            pricingType: 'default',
            input: crossSellItems
              .map((item) => item.linked_product_sku)
              .join(' '),
            filterType: 'CG',
            setorAtividadeList: divisions.map((cod) => parseInt(cod))
          }
        },
        { headers, params }
      );

      if (
        response.data &&
        response.data.items &&
        Array.isArray(response.data.items)
      ) {
        return { items: response.data.items ?? response.data };
      }

      return { items: [] };
    } catch (error) {
      console.error('Erro ao buscar produtos com preços:', error);
      return { items: [] };
    }
  }
}
